import React, { useState, useEffect } from "react"
import { Menu, X, Home, ArrowLeft, Check } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { useLanguage } from "./LanguageContext" // Assuming you have this
import { useTheme } from "./ThemeManager"

// Import CSS - add this to your app entry point or component
// This example assumes you have these CSS files at the root level
// import './css/theme-variables.css';
// import './css/base-styles.css';
// import './css/components-styles.css';

const removeEmojis = (text) => {
  if (!text) return ""

  // This regex pattern matches emoji characters
  return text
    .replace(
      /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu,
      "",
    )
    .trim()
}

// Navigation translations
const navigationTranslations = {
  en: {
    navigation: "Navigation",
    backToAllGames: "Back",
    home: "Home",
    start: "Start",
  },
  nl: {
    navigation: "Navigatie",
    backToAllGames: "Terug",
    home: "Home",
    start: "Start",
  },
}

// Add a function to hide mobile quiz feedback when navigating
const hideMobileFeedback = () => {
  const feedbackEl = document.getElementById("mobile-quiz-feedback")
  if (feedbackEl) {
    feedbackEl.classList.remove("active")
  }
}

const NavigationMenu = ({
  sections,
  activeSection,
  setActiveSection,
  setCurrentStep,
  score,
  streak,
  isHomeActive,
  onReturnHome,
  gameConfig,
  setSelectedAnswer,
  setIsChecking,
}) => {
  const { language } = useLanguage()
  const { currentTheme, currentLayout } = useTheme() // Access theme context
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Get translations
  const nav = navigationTranslations[language.code] || navigationTranslations.en

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Initial check
    checkMobile()

    // Add resize listener
    window.addEventListener("resize", checkMobile)

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  return (
    <>
      {/* Unified Navigation Bar - always visible and sticky */}
      <div className={`nav-unified ${isMobile ? "nav-mobile" : "nav-desktop"}`}>
        <div className="nav-wrapper">
          <div className="nav-content">
            {/* Back button - X on mobile, text+arrow on desktop */}
            <div className="nav-back-container">
              {isMobile ? (
                <button onClick={onReturnHome} className="nav-back-button-mobile">
                  <X className="nav-back-icon" />
                </button>
              ) : (
                <button onClick={onReturnHome} className="nav-back-button">
                  <ArrowLeft className="nav-back-icon" />
                  <span className="nav-back-text">{nav.backToAllGames}</span>
                </button>
              )}
            </div>

            {/* Scrollable navigation area - only visible on desktop or when menu is open on mobile */}
            <div className={`nav-scrollable-area ${isMobile ? "mobile-hidden" : ""}`}>
              <div className="nav-items-container">
                {/* Divider */}
                <div className="nav-divider"></div>

                {/* Home Button */}
                <button
                  onClick={() => {
                    hideMobileFeedback()
                    setActiveSection(0)
                    setCurrentStep(0)
                    if (isMobile) setIsOpen(false)
                  }}
                  className={`nav-home-button ${isHomeActive ? "active" : ""}`}
                >
                  <div className={`nav-home-icon-container ${isHomeActive ? "active" : ""}`}>
                    <Home className="nav-home-icon" />
                  </div>
                  <span className="nav-home-text">{nav.start}</span>
                </button>

                {/* Section Steps */}
                {sections
                  .filter(
                    (section) =>
                      !section.hideFromNavigation &&
                      !section.key.includes("celebration") &&
                      section.key !== "success" &&
                      section.key !== "intro",
                  )
                  .map((section, index) => {
                    const actualIndex = sections.findIndex((s) => s.key === section.key)
                    const isActive = activeSection === actualIndex
                    const isCompleted = actualIndex < activeSection

                    // Remove emojis from section title
                    const cleanTitle = removeEmojis(section.title)

                    return (
                      <React.Fragment key={section.key}>
                        {/* Connector line */}
                        <div className="nav-connector"></div>

                        <button
                          onClick={() => {
                            if (actualIndex !== activeSection) {
                              hideMobileFeedback()
                              setActiveSection(actualIndex)
                              setCurrentStep(0)
                              setSelectedAnswer(null)
                              setIsChecking(false)
                              if (isMobile) setIsOpen(false)
                            }
                          }}
                          className={`nav-section-button ${isActive ? "active" : isCompleted ? "completed" : ""}`}
                        >
                          <div
                            className={`nav-section-indicator ${
                              isActive ? "active" : isCompleted ? "completed" : "inactive"
                            }`}
                          >
                            {isCompleted ? (
                              <Check className="nav-section-check" />
                            ) : (
                              <span className="nav-section-number">{index + 1}</span>
                            )}
                          </div>
                          <span
                            className={`nav-section-text ${
                              isActive ? "active" : isCompleted ? "completed" : "inactive"
                            }`}
                          >
                            {cleanTitle}
                          </span>
                        </button>
                      </React.Fragment>
                    )
                  })}
              </div>
            </div>

            {/* Left and right scrolling gradients - only on desktop */}
            {!isMobile && (
              <>
                <div className="nav-gradient-left"></div>
                <div className="nav-gradient-right"></div>
              </>
            )}

            {/* Indicators and hamburger menu */}
            <div className="nav-indicators">
              {streak > 0 && (
                <div className="nav-streak-indicator">
                  <span className="nav-streak-text">✨ {streak}</span>
                </div>
              )}
              {score > 0 && (
                <div className="nav-score-indicator">
                  <span className="nav-score-text">⭐️ {score}</span>
                </div>
              )}
              {isMobile && (
                <button onClick={() => setIsOpen(!isOpen)} className="nav-menu-button">
                  <Menu className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu Overlay - only shown when mobile menu is open */}
      {isMobile && (
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="nav-overlay"
                onClick={() => setIsOpen(false)}
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25 }}
                className="nav-sidebar"
              >
                <div className="nav-sidebar-header">
                  <h2 className="nav-sidebar-title">{nav.navigation}</h2>
                  <button onClick={() => setIsOpen(false)} className="nav-sidebar-close">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="nav-sidebar-content">
                  <button onClick={onReturnHome} className="nav-mobile-back">
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">{nav.backToAllGames}</span>
                  </button>
                  <button
                    onClick={() => {
                      hideMobileFeedback()
                      setActiveSection(0)
                      setCurrentStep(0)
                      setIsOpen(false)
                    }}
                    className={`nav-mobile-home ${isHomeActive ? "active" : "inactive"}`}
                  >
                    <Home className="w-5 h-5" />
                    <span className="font-medium">{nav.home}</span>
                  </button>

                  {sections
                    .filter(
                      (section) =>
                        !section.hideFromNavigation &&
                        !section.key.includes("celebration") &&
                        section.key !== "success" &&
                        section.key !== "intro",
                    )
                    .map((section) => {
                      const actualIndex = sections.findIndex((s) => s.key === section.key)
                      const isActive = activeSection === actualIndex
                      const isCompleted = actualIndex < activeSection

                      return (
                        <button
                          key={section.key}
                          onClick={() => {
                            if (actualIndex !== activeSection) {
                              hideMobileFeedback()
                              setActiveSection(actualIndex)
                              setCurrentStep(0)
                              setIsOpen(false)
                            }
                          }}
                          className={`nav-mobile-section ${isActive ? "active" : isCompleted ? "completed" : "inactive"}`}
                        >
                          <span className="nav-mobile-section-title">{section.title}</span>
                          {isCompleted && (
                            <div className="nav-mobile-check-container">
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="nav-mobile-check"
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            </div>
                          )}
                        </button>
                      )
                    })}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}

     
    </>
  )
}

export default NavigationMenu

